/**
 * Step progress indicator for the verification flow.
 *
 * Displays a horizontal sequence of numbered steps on tablet/desktop.
 * On mobile (below `sm:` breakpoint) shows only the current step label
 * with a compact "Step X of Y" display to avoid horizontal overflow.
 *
 * Step labels are looked up from the `portal` translation namespace
 * via `stepIndicator.steps.<key>`. Callers may pass custom Step
 * arrays with arbitrary `key` values; unknown keys fall back to the
 * provided `label` for backwards compatibility.
 *
 * @module components/StepIndicator
 */

import { useTranslation } from 'react-i18next';

export interface Step {
  label: string;
  key: string;
}

export const VERIFICATION_STEPS: Step[] = [
  { key: 'start', label: 'Start' },
  { key: 'details', label: 'Details' },
  { key: 'documents', label: 'Documents' },
  { key: 'selfie', label: 'Selfie' },
  { key: 'complete', label: 'Complete' },
];

interface StepIndicatorProps {
  /** The `key` of the currently active step. */
  currentStep: string;
  /** Steps to display. Defaults to {@link VERIFICATION_STEPS}. */
  steps?: Step[];
}

export function StepIndicator({
  currentStep,
  steps = VERIFICATION_STEPS,
}: StepIndicatorProps) {
  const { t } = useTranslation('portal');
  const currentIndex = steps.findIndex((s) => s.key === currentStep);
  const hasCurrentStep = currentIndex >= 0;

  // Resolve a translated label for a step key, falling back to the
  // provided label when no translation exists.
  const labelFor = (step: Step) =>
    t(`stepIndicator.steps.${step.key}`, { defaultValue: step.label });

  const currentLabel = hasCurrentStep
    ? labelFor(steps[currentIndex])
    : t('stepIndicator.progressUnavailable');
  const currentStepNumber = hasCurrentStep ? currentIndex + 1 : undefined;

  // Two-digit mono step labels: 01, 02, ... — the data-product signature.
  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <nav aria-label={t('stepIndicator.ariaLabel')} className="w-full py-5">
      {/* Mobile: mono "step XX / YY" + current label + hairline progress bar */}
      <div className="sm:hidden">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-xs uppercase tracking-[0.12em] text-ink-muted">
            {currentStepNumber
              ? t('stepIndicator.stepCount', {
                  current: pad(currentStepNumber),
                  total: pad(steps.length),
                })
              : t('stepIndicator.stepsTotal', { total: pad(steps.length) })}
          </span>
          <span className="text-sm font-semibold text-ink">{currentLabel}</span>
        </div>
        {/* Hairline track with primary fill */}
        <div
          className="mt-3 h-px w-full bg-hairline-strong"
          aria-hidden="true"
        >
          <div
            className="h-px bg-[var(--nv-primary-color)] transition-all duration-300"
            style={{
              inlineSize: hasCurrentStep
                ? `${((currentIndex + 1) / steps.length) * 100}%`
                : '0%',
            }}
          />
        </div>
      </div>

      {/* Tablet / Desktop: numbered steps over a single hairline track */}
      <ol className="hidden items-stretch sm:flex">
        {steps.map((step, index) => {
          const isCompleted = hasCurrentStep && index < currentIndex;
          const isCurrent = hasCurrentStep && index === currentIndex;
          const statusText = isCurrent
            ? t('stepIndicator.currentStep')
            : isCompleted
              ? t('stepIndicator.completedStatus')
              : t('stepIndicator.notStarted');

          return (
            <li key={step.key} className="flex flex-1 flex-col">
              <div className="flex items-baseline gap-2">
                {/* Mono step number */}
                <span
                  className={[
                    'font-mono text-sm tabular-nums transition-colors duration-200',
                    isCurrent
                      ? 'font-medium text-[var(--nv-primary-color)]'
                      : isCompleted
                        ? 'text-ink-muted'
                        : 'text-ink-muted',
                  ].join(' ')}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isCompleted ? (
                    <svg
                      className="h-3.5 w-3.5 translate-y-0.5 text-[var(--nv-primary-color)]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    pad(index + 1)
                  )}
                </span>
                <span
                  className={[
                    'truncate text-sm transition-colors duration-200',
                    isCurrent
                      ? 'font-semibold text-ink'
                      : isCompleted
                        ? 'text-ink-muted'
                        : 'text-ink-muted',
                  ].join(' ')}
                >
                  {labelFor(step)}
                  <span className="sr-only">
                    , {isCompleted ? t('stepIndicator.completed') : statusText}
                  </span>
                </span>
              </div>
              {/* Hairline track segment; primary once reached */}
              <div
                className={[
                  'mt-2.5 h-0.5 w-full transition-colors duration-300',
                  index <= currentIndex && hasCurrentStep
                    ? 'bg-[var(--nv-primary-color)]'
                    : 'bg-hairline-strong',
                ].join(' ')}
                aria-hidden="true"
              />
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
